import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUsers, FaCar, FaClipboardList, FaUserCog } from 'react-icons/fa';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import api from '../../utils/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const initialDashboardData = {
  totalClients: 0,
  totalVehicles: 0,
  totalOrders: 0,
  totalTechnicians: 0,
  serviceOrdersOverTime: [],
  topServices: [],
  orderStatusDistribution: [],
  recentOrders: {
    data: [],
    total: 0,
    currentPage: 1,
    perPage: 10,
    lastPage: 1,
  },
};

export default function Dashboard() {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(initialDashboardData);
  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  const fetchDashboardData = useCallback(async (start = null, end = null, page = 1) => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard', { 
        params: { 
          start, 
          end,
          page,
          per_page: ordersPerPage
        } 
      });
      setDashboardData(prevData => ({
        ...prevData,
        ...response.data,
        recentOrders: {
          ...response.data.recentOrders,
          data: response.data.recentOrders.data,
        },
      }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [ordersPerPage]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (newPage <= dashboardData.recentOrders.lastPage) {
      fetchDashboardData(null, null, newPage);
    }
  };

  const applyDateFilter = () => {
    const [start, end] = dateRange;
    fetchDashboardData(start?.toISOString(), end?.toISOString());
  };

  if (loading && currentPage === 1) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-100 min-h-screen">
      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title={t('total_clients')} value={dashboardData.totalClients} icon={<FaUsers className="text-5xl text-blue-500 opacity-80" />} />
        <StatCard title={t('total_vehicles')} value={dashboardData.totalVehicles} icon={<FaCar className="text-5xl text-green-500 opacity-80" />} />
        <StatCard title={t('total_orders')} value={dashboardData.totalOrders} icon={<FaClipboardList className="text-5xl text-yellow-500 opacity-80" />} />
        <StatCard title={t('total_technicians')} value={dashboardData.totalTechnicians} icon={<FaUserCog className="text-5xl text-purple-500 opacity-80" />} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title={t('service_orders_over_time')} data={dashboardData.serviceOrdersOverTime} />
        <ChartCard title={t('top_services')} data={dashboardData.topServices} />
      </div>

      {/* Recent Orders Table */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">{t('recent_orders')}</h2>
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left font-semibold text-gray-700">{t('order_id')}</th>
              <th className="p-2 text-left font-semibold text-gray-700">{t('client')}</th>
              <th className="p-2 text-left font-semibold text-gray-700">{t('vehicle')}</th>
              <th className="p-2 text-left font-semibold text-gray-700">{t('service')}</th>
              <th className="p-2 text-left font-semibold text-gray-700">{t('status')}</th>
            </tr>
          </thead>
          <tbody>
            {dashboardData.recentOrders.data.map((order) => (
              <tr key={order.id} className="border-b border-gray-200">
                <td className="p-2">{order.id}</td>
                <td className="p-2">{order.client_name}</td>
                <td className="p-2">{order.vehicle_model}</td>
                <td className="p-2">{order.service_name}</td>
                <td className="py-3 px-6 text-center min-w-[150px]">
                <span className={`px-2 py-1 rounded-full text-xs ${
                      order.status === 'completed' ? 'bg-green-200 text-green-800' :
                      order.status === 'in_progress' ? 'bg-blue-200 text-blue-800' :
                      order.status === 'cancelled' ? 'bg-red-200 text-red-800' :
                      'bg-yellow-200 text-yellow-800'
                    }`}>
                  {t(order.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex justify-between items-center">
          <p>{t('showing')} {((currentPage - 1) * ordersPerPage) + 1} {t('to')} {Math.min(currentPage * ordersPerPage, dashboardData.recentOrders.total)} {t('of')} {dashboardData.recentOrders.total} {t('results')}</p>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-md bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              {t('previous')}
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= dashboardData.recentOrders.lastPage}
              className="px-4 py-2 border rounded-md bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              {t('next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
          <p className="text-3xl font-bold text-blue-600">{value}</p>
        </div>
        {icon}
      </div>
    </div>
  );
}

function ChartCard({ title, data }) {
  const { t } = useTranslation();

  if (title === t('service_orders_over_time')) {
    const chartData = {
      labels: data.map(item => item.date),
      datasets: [{
        label: t('service_orders'),
        data: data.map(item => item.count),
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1
      }]
    };

    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>
        <Line data={chartData} />
      </div>
    );
  } else if (title === t('top_services')) {
    const chartData = {
      labels: data.map(item => item.service_name),
      datasets: [{
        label: t('service_count'),
        data: data.map(item => item.count),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      }]
    };

    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>
        <Bar data={chartData} />
      </div>
    );
  }

  return null;
}

function getStatusClass(status) {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800';
    case 'pending':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

